import React, { useState, useCallback } from "react";
import { toast } from "react-hot-toast";
import { HiOutlineChevronDown, HiOutlineChevronRight, HiOutlineExternalLink, HiOutlinePlay, HiOutlineX } from "react-icons/hi";
import API from "../../api";
import { EFFECT_TYPE_LABELS, EFFECT_TYPE_STYLES } from "../../utils";
import CodeLookup from "../../components/CodeLookup";
import LaboratoryLookup from "../../components/LaboratoryLookup";
import MutuelleCodeLookup from "../../components/MutuelleCodeLookup";
import QuestionLookup from "../../components/QuestionLookup";
import { capture } from "../../instrument";

const DAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
const DAY_LABELS = {
  MONDAY: "Lundi",
  TUESDAY: "Mardi",
  WEDNESDAY: "Mercredi",
  THURSDAY: "Jeudi",
  FRIDAY: "Vendredi",
  SATURDAY: "Samedi",
  SUNDAY: "Dimanche",
};

const formatTriggerValue = (value) => {
  if (value === null || value === undefined) return "null";
  if (Array.isArray(value)) return value.length > 0 ? value.join(", ") : "(vide)";
  return String(value);
};

const Simulator = () => {
  const [codes, setCodes] = useState([]);
  const [sex, setSex] = useState("");
  const [age, setAge] = useState("");
  const [admissionType, setAdmissionType] = useState("LATER");
  const [prescriptionStatus, setPrescriptionStatus] = useState("PRESENT");
  const [patientStatus, setPatientStatus] = useState("UNKNOWN");
  const [insStatus, setInsStatus] = useState("NON_QUALIFIED");
  const [mutuelleStatus, setMutuelleStatus] = useState("UNKNOWN");
  const [mutuelleCode, setMutuelleCode] = useState("");
  const [laboratoryId, setLaboratoryId] = useState("");
  const [flags, setFlags] = useState([]);
  const [flagInput, setFlagInput] = useState("");
  const [day, setDay] = useState("");
  const [hour, setHour] = useState("");
  const [sameDayPatientRank, setSameDayPatientRank] = useState("");
  const [source, setSource] = useState("PATIENT");
  const [questionAnswers, setQuestionAnswers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const handleSimulate = async () => {
    setLoading(true);
    try {
      const body = {
        source,
        codes: codes.map((c) => c.value || c),
        patient: {
          sex,
          age: age !== "" ? parseInt(age, 10) : undefined,
          status: patientStatus,
          insStatus,
          mutuelleStatus,
          mutuelleCode: mutuelleCode || undefined,
        },
        admission: {
          type: admissionType,
          prescriptionStatus,
          flags,
          laboratoryId: laboratoryId || undefined,
          day: day || undefined,
          hour: hour || undefined,
          sameDayPatientRank: Number.isInteger(Number(sameDayPatientRank)) && Number(sameDayPatientRank) >= 1 ? Number(sameDayPatientRank) : undefined,
        },
        questionAnswers: questionAnswers.map((qa) => ({
          questionId: qa.questionId,
          optionValue: qa.answer,
        })),
      };

      const res = await API.post("/simulator/evaluate", body);
      if (!res.ok) return toast.error("Erreur lors de la simulation");
      setResults(res.data);
    } catch (error) {
      capture(error);
      toast.error("Erreur lors de la simulation");
    } finally {
      setLoading(false);
    }
  };

  const addQuestionAnswer = useCallback((question, answer, optionValue = answer) => {
    setQuestionAnswers((prev) => {
      const existing = prev.find((qa) => qa.questionId === question._id);
      if (existing) {
        return prev.map((qa) => (qa.questionId === question._id ? { ...qa, answer, optionValue, question: question.question, questionCode: question.questionCode } : qa));
      }
      return [
        ...prev,
        { questionId: question._id, answer, optionValue, question: question.question, questionCode: question.questionCode, answerType: question.answerType, options: question.options },
      ];
    });
  }, []);

  const removeQuestionAnswer = useCallback((questionId) => {
    setQuestionAnswers((prev) => prev.filter((qa) => qa.questionId !== questionId));
  }, []);

  const addFlag = (value) => {
    const trimmed = value.trim();
    if (!trimmed || flags.includes(trimmed)) return;
    setFlags([...flags, trimmed]);
    setFlagInput("");
  };

  const lastIteration = results?.results[results.results.length - 1];

  return (
    <div className="flex h-[calc(100vh-60px)] flex-col bg-gray-50">
      {/* Page Header */}
      <div className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
        <h1 className="text-xl font-semibold text-gray-900">Simulateur</h1>
      </div>

      {/* Content Grid */}
      <div className="flex flex-1 overflow-hidden p-6">
        {/* Form Panel */}
        <div className="flex w-72 shrink-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="flex-1 overflow-y-auto">
            {/* Section: Simulation */}
            <div className="space-y-4 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Simulation</p>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">Codes d'actes</label>
                <CodeLookup onChange={setCodes} initialValues={codes} placeholder="Rechercher des codes..." />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">Ordonnance</label>
                <select value={prescriptionStatus} onChange={(e) => setPrescriptionStatus(e.target.value)} className="w-full rounded-md border border-gray-300 p-1.5 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500">
                  <option value="PRESENT">Présente</option>
                  <option value="ABSENT">Absente</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">Source</label>
                <select value={source} onChange={(e) => setSource(e.target.value)} className="w-full rounded-md border border-gray-300 p-1.5 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500">
                  <option value="PATIENT">Patient</option>
                  <option value="IDE">IDE</option>
                </select>
              </div>
            </div>

            <div className="border-t border-gray-100" />

            {/* Section: Patient */}
            <div className="space-y-4 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Patient</p>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">Sexe</label>
                <select value={sex} onChange={(e) => setSex(e.target.value)} className="w-full rounded-md border border-gray-300 p-1.5 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500">
                  <option value="">-- Non défini --</option>
                  <option value="M">Masculin</option>
                  <option value="F">Féminin</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">Âge</label>
                <input type="number" min="0" max="150" value={age} onChange={(e) => setAge(e.target.value)} placeholder="Ex: 45" className="w-full rounded-md border border-gray-300 p-1.5 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">Statut patient</label>
                <select value={patientStatus} onChange={(e) => setPatientStatus(e.target.value)} className="w-full rounded-md border border-gray-300 p-1.5 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500">
                  <option value="UNKNOWN">Inconnu</option>
                  <option value="KNOWN">Connu</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">Statut INS</label>
                <select value={insStatus} onChange={(e) => setInsStatus(e.target.value)} className="w-full rounded-md border border-gray-300 p-1.5 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500">
                  <option value="NON_QUALIFIED">Non qualifié</option>
                  <option value="QUALIFIED">Qualifié</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">Statut mutuelle</label>
                <select value={mutuelleStatus} onChange={(e) => setMutuelleStatus(e.target.value)} className="w-full rounded-md border border-gray-300 p-1.5 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500">
                  <option value="UNKNOWN">Inconnu</option>
                  <option value="OUTDATED">Expiré</option>
                  <option value="UP_TO_DATE">À jour</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">Code mutuelle</label>
                <MutuelleCodeLookup onChange={(tags) => setMutuelleCode(tags[0] || "")} initialValues={mutuelleCode ? [mutuelleCode] : []} placeholder="Code mutuelle" />
              </div>
            </div>

            <div className="border-t border-gray-100" />

            {/* Section: Admission */}
            <div className="space-y-4 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Admission</p>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">Type d'admission</label>
                <select value={admissionType} onChange={(e) => setAdmissionType(e.target.value)} className="w-full rounded-md border border-gray-300 p-1.5 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500">
                  <option value="LATER">LATER</option>
                  <option value="NOW">NOW</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">Laboratoire</label>
                <LaboratoryLookup onChange={(ids) => setLaboratoryId(ids[0] || "")} initialValues={laboratoryId ? [laboratoryId] : []} placeholder="Sélectionner un laboratoire" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">Flags</label>
                {flags.length > 0 && (
                  <div className="mb-1.5 flex flex-wrap gap-1">
                    {flags.map((flag) => (
                      <span
                        key={flag}
                        className="inline-flex items-center gap-1 rounded-md border border-primary-100 bg-primary-50 px-1.5 py-0.5 text-xs font-medium text-primary-700"
                      >
                        {flag}
                        <button type="button" onClick={() => setFlags(flags.filter((f) => f !== flag))} className="text-primary-400 transition-colors hover:text-primary-700">
                          <HiOutlineX className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <input
                  type="text"
                  value={flagInput}
                  onChange={(e) => setFlagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addFlag(flagInput);
                    }
                  }}
                  placeholder="Ajouter un flag (Entrée)"
                  className="w-full rounded-md border border-gray-300 p-1.5 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div className="border-t border-gray-100" />

            {/* Section: Réponses aux questions */}
            <div className="space-y-4 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Réponses aux questions</p>
              <QuestionAnswerForm questionAnswers={questionAnswers} onAdd={addQuestionAnswer} onRemove={removeQuestionAnswer} />
            </div>

            <div className="border-t border-gray-100" />

            {/* Section: Planification */}
            <div className="space-y-4 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Planification</p>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">Jour</label>
                <select value={day} onChange={(e) => setDay(e.target.value)} className="w-full rounded-md border border-gray-300 p-1.5 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500">
                  <option value="">-- Aujourd'hui --</option>
                  {DAYS.map((d) => (
                    <option key={d} value={d}>
                      {DAY_LABELS[d]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">Heure</label>
                <input type="time" value={hour} onChange={(e) => setHour(e.target.value)} className="w-full rounded-md border border-gray-300 p-1.5 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">Rang de l'admission le même jour</label>
                <input type="number" min="1" value={sameDayPatientRank} onChange={(e) => setSameDayPatientRank(e.target.value)} placeholder="Ex: 2 pour la 2ᵉ admission du jour" className="w-full rounded-md border border-gray-300 p-1.5 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500" />
              </div>
            </div>
          </div>

          {/* Sticky footer */}
          <div className="border-t border-gray-100 p-4">
            <button
              type="button"
              onClick={handleSimulate}
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Simulation en cours...
                </>
              ) : (
                <>
                  <HiOutlinePlay className="h-4 w-4" />
                  Simuler
                </>
              )}
            </button>
          </div>
        </div>

        {/* Results Panel */}
        <div className="flex-1 overflow-y-auto">
          {!results && !loading && (
            <div className="flex h-full flex-col items-center justify-center text-gray-400">
              <HiOutlinePlay className="mb-3 h-8 w-8 text-gray-300" />
              <p className="text-sm">Configurez un dossier virtuel et lancez la simulation</p>
              <p className="mt-1 text-xs">Les résultats apparaîtront ici</p>
            </div>
          )}

          {loading && (
            <div className="flex h-full flex-col items-center justify-center gap-3">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
              <span className="text-sm text-gray-400">Simulation en cours...</span>
            </div>
          )}

          {results && !loading && lastIteration && (
            <div className="space-y-4 px-8">
              {/* Summary Banner */}
              <div className={`rounded-lg border px-4 py-3 ${results.stabilized ? "border-gray-100 bg-white" : "border-orange-200 bg-orange-50"}`}>
                <div className="flex items-center gap-4 text-sm">
                  <span className={results.stabilized ? "text-gray-700" : "font-medium text-orange-700"}>
                    {results.stabilized
                      ? `Stabilisé en ${results.iterations} itération${results.iterations > 1 ? "s" : ""}`
                      : `Non stabilisé après ${results.iterations} itérations`}
                  </span>
                  <div className="ml-auto flex gap-4 text-gray-500">
                    <span className="tabular-nums">
                      {results.summary.triggeredQuestions}/{results.summary.totalQuestions} questions
                    </span>
                    <span className="tabular-nums">
                      {results.summary.triggeredGuidelines}/{results.summary.totalGuidelines} recommandations
                    </span>
                    <span className="tabular-nums">
                      {results.summary.triggeredActions}/{results.summary.totalActions} actions
                    </span>
                  </div>
                </div>
              </div>

              {/* Code Changes */}
              {(results.summary.codesAdded.length > 0 || results.summary.codesRemoved.length > 0 || results.summary.incompatibilities?.length > 0) && (
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                  <div className="border-b border-gray-100 px-4 py-3">
                    <h3 className="text-sm font-medium text-gray-900">Modifications des codes</h3>
                  </div>
                  <div className="flex flex-wrap gap-2 p-4">
                    {results.summary.codesAdded.map((code) => (
                      <span key={code} className="rounded-md bg-green-100 px-2 py-0.5 text-sm font-medium text-green-800">
                        + {code}
                      </span>
                    ))}
                    {results.summary.codesRemoved.map((code) => (
                      <span key={code} className="rounded-md bg-red-100 px-2 py-0.5 text-sm font-medium text-red-800">
                        - {code}
                      </span>
                    ))}
                    {(results.summary.incompatibilities || []).map(([codeA, codeB]) => (
                      <span key={`${codeA}:${codeB}`} className="rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-sm font-medium text-red-700">
                        {codeA} incompatible avec {codeB}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Questions */}
              <ResultSection
                title="Questions"
                items={lastIteration.questions}
                labelKey="question"
                audiencesKey="audiences"
                routePrefix="/question"
                questionAnswers={questionAnswers}
                onAnswerQuestion={addQuestionAnswer}
              />

              {/* Guidelines */}
              <ResultSection title="Recommandations" items={lastIteration.guidelines} labelKey="guideline" audiencesKey="audiences" activeKey="activeAfterGrouping" routePrefix="/guideline" />

              {/* Actions */}
              <ResultSection title="Actions" items={lastIteration.actions} labelKey="name" audiencesKey={null} routePrefix="/action">
                {(action) => (
                  <div className="ml-auto flex items-center gap-2 pl-4">
                    {action.effects?.map((effect, i) => (
                      <span key={i} className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${EFFECT_TYPE_STYLES[effect.type] || "bg-gray-50 text-gray-700"}`}>
                        {EFFECT_TYPE_LABELS[effect.type] || effect.type}
                        {effect.values?.length > 0 && <span className="ml-1 opacity-70">({effect.values.length})</span>}
                      </span>
                    ))}
                  </div>
                )}
              </ResultSection>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ResultSection = ({ title, items, labelKey, audiencesKey, activeKey, routePrefix, children, questionAnswers, onAnswerQuestion }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [answerDrafts, setAnswerDrafts] = useState({});

  const triggeredCount = items.filter((i) => (activeKey ? i[activeKey] : i.triggered)).length;

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredItems = normalizedQuery
    ? items.filter((item) => [item[labelKey], item.questionCode, item._id].some((field) => String(field ?? "").toLowerCase().includes(normalizedQuery)))
    : items;

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const setDraft = (id, value) => setAnswerDrafts((prev) => ({ ...prev, [id]: value }));

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
        <h3 className="text-sm font-medium text-gray-900">{title}</h3>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Libellé, code, ID…"
          className="ml-auto w-40 rounded-md border border-gray-200 px-2 py-0.5 text-xs text-gray-900 outline-none placeholder:text-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
        />
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-400">
          {searchQuery ? `${filteredItems.length}/${items.length}` : `${triggeredCount}/${items.length}`}
        </span>
      </div>

      {items.length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-400">Aucun élément configuré</div>
      ) : filteredItems.length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-400">Aucun résultat pour cette recherche</div>
      ) : (
        <div className="max-h-[500px] divide-y divide-gray-100 overflow-y-auto">
          {[...filteredItems]
            .sort((a, b) => {
              const aActive = activeKey ? a[activeKey] : a.triggered;
              const bActive = activeKey ? b[activeKey] : b.triggered;
              return bActive - aActive;
            })
            .map((item) => {
              const isActive = activeKey ? item[activeKey] : item.triggered;
              const isQuestion = item.answerType !== undefined;
              const currentAnswer = isQuestion ? questionAnswers?.find((qa) => qa.questionId === item._id) : null;
              const expanded = expandedIds.has(item._id);
              const draftValue = answerDrafts[item._id];
              const multipleChoiceSelectedValue = currentAnswer?.answer || "";
              let displayAnswer = currentAnswer?.optionValue || "";
              if (item.answerType === "MULTIPLE_CHOICE") displayAnswer = multipleChoiceSelectedValue;
              if (draftValue !== undefined) displayAnswer = draftValue;
              const commitAnswer = (value) => {
                setDraft(item._id, value);
                if (!value) return;
                if (item.answerType === "MULTIPLE_CHOICE") {
                  const option = item.options?.find((currentOption) => currentOption._id === value);
                  onAnswerQuestion(item, value, option?.value || value);
                  return;
                }
                onAnswerQuestion(item, value, value);
              };

              return (
                <div key={item._id}>
                  <div
                    className={`flex cursor-pointer items-center px-4 py-3 transition-colors hover:bg-gray-50 ${!isActive ? "opacity-50" : ""}`}
                    onClick={() => toggleExpanded(item._id)}
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      {expanded ? (
                        <HiOutlineChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
                      ) : (
                        <HiOutlineChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
                      )}

                      <span
                        className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}
                      >
                        {isActive ? "Actif" : "Inactif"}
                      </span>

                      {labelKey && item[labelKey] && <span className="truncate text-sm text-gray-900">{item[labelKey]}</span>}
                      {children && typeof children === "function" && children(item)}
                    </div>

                    <div className="ml-auto flex shrink-0 items-center gap-2 pl-4">
                      {currentAnswer && <span className="rounded-md bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700">{currentAnswer.optionValue}</span>}
                      {audiencesKey && Array.isArray(item[audiencesKey]) && item[audiencesKey].length > 0 && (
                        <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">{item[audiencesKey].join(" / ")}</span>
                      )}
                      {routePrefix && item._id && (
                        <a
                          href={`${routePrefix}/${item._id}/edit`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          title="Ouvrir dans un nouvel onglet"
                          className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                        >
                          <HiOutlineExternalLink className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Inline answer UI for triggered questions */}
                  {expanded && isQuestion && onAnswerQuestion && (
                    <div className="border-t border-gray-100 px-4 py-2">
                      <div className="flex items-center gap-2">
                        <span className="shrink-0 text-xs font-medium text-gray-500">Réponse :</span>
                        <div className="flex-1">
                          {item.answerType === "MULTIPLE_CHOICE" && item.options?.length > 0 ? (
                            <select value={displayAnswer} onChange={(e) => commitAnswer(e.target.value)} onClick={(e) => e.stopPropagation()} className="w-full rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-900 outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500">
                              <option value="">-- Choisir --</option>
                              {item.options.map((option) => (
                                <option key={option._id} value={option._id}>
                                  {option.value}
                                </option>
                              ))}
                            </select>
                          ) : item.answerType === "DATE" ? (
                            <input
                              type="date"
                              value={displayAnswer}
                              onChange={(e) => commitAnswer(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-900 outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                            />
                          ) : item.answerType === "HOUR" ? (
                            <input
                              type="time"
                              value={displayAnswer}
                              onChange={(e) => commitAnswer(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-900 outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                            />
                          ) : item.answerType === "DOCUMENT" ? (
                            <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                onClick={() => onAnswerQuestion(item, "PRESENT", "Document fourni")}
                                className={`rounded-md border px-2 py-1 text-xs font-medium transition-colors ${currentAnswer?.answer === "PRESENT" ? "border-primary-500 bg-primary-500 text-white" : "border-gray-200 bg-white text-gray-700 hover:border-primary-500 hover:text-primary-700"}`}
                              >
                                Document fourni
                              </button>
                              <button
                                type="button"
                                onClick={() => onAnswerQuestion(item, "", "Document non fourni")}
                                className={`rounded-md border px-2 py-1 text-xs font-medium transition-colors ${currentAnswer && currentAnswer.answer === "" ? "border-primary-500 bg-primary-500 text-white" : "border-gray-200 bg-white text-gray-700 hover:border-primary-500 hover:text-primary-700"}`}
                              >
                                Document non fourni
                              </button>
                            </div>
                          ) : (
                            <input
                              type="text"
                              value={displayAnswer}
                              onChange={(e) => setDraft(item._id, e.target.value)}
                              onBlur={() => {
                                if (displayAnswer) commitAnswer(displayAnswer);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && displayAnswer) commitAnswer(displayAnswer);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Saisir la réponse..."
                              className="w-full rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-900 outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {expanded && item.evaluation && (
                    <div className="px-4 pb-3">
                      <div className="space-y-2 rounded-lg border border-gray-100 bg-gray-50 p-3">
                        <div className="mb-2 flex items-center gap-2 text-xs text-gray-500">
                          <span className="font-medium">Opérateur entre groupes :</span>
                          <span className="rounded bg-gray-200 px-1.5 py-0.5 font-mono text-gray-700">{item.evaluation.groupsOperator}</span>
                        </div>

                        {item.evaluation.groups.map((group, groupIndex) => (
                          <div key={groupIndex}>
                            {groupIndex > 0 && (
                              <div className="flex items-center gap-2 py-1">
                                <div className="flex-1 border-t border-gray-200" />
                                <span className="font-mono text-xs text-gray-400">{item.evaluation.groupsOperator}</span>
                                <div className="flex-1 border-t border-gray-200" />
                              </div>
                            )}
                            <div className={`rounded-md border p-2 ${group.result ? "border-green-200 bg-green-50/50" : "border-red-200 bg-red-50/50"}`}>
                              <div className="mb-1.5 flex items-center gap-2">
                                <span className={`text-xs font-medium ${group.result ? "text-green-700" : "text-red-700"}`}>Groupe {groupIndex + 1}</span>
                                <span className="text-xs text-gray-500">
                                  op: <span className="font-mono">{group.operator}</span>
                                </span>
                                <span className={`ml-auto text-xs font-medium ${group.result ? "text-green-700" : "text-red-700"}`}>{group.result ? "PASS" : "FAIL"}</span>
                              </div>

                              <div className="space-y-1">
                                {group.triggers.map((trigger, triggerIndex) => (
                                  <div
                                    key={triggerIndex}
                                    className={`flex items-start gap-2 rounded px-2 py-1.5 text-xs ${trigger.result ? "bg-green-100/50" : "bg-red-100/50"}`}
                                  >
                                    <span className={`shrink-0 font-medium ${trigger.result ? "text-green-700" : "text-red-700"}`}>{trigger.result ? "PASS" : "FAIL"}</span>

                                    <div className="min-w-0 flex-1">
                                      <div className="flex flex-wrap items-center gap-1.5">
                                        <span className="rounded border border-gray-200 bg-white px-1 py-0.5 font-mono text-gray-700">{trigger.type}</span>
                                        {(trigger.fieldLabel || trigger.field) && <span className="text-gray-500">{trigger.fieldLabel || trigger.field}</span>}
                                        {trigger.negate && <span className="rounded bg-orange-100 px-1 py-0.5 text-[10px] font-medium text-orange-700">NEGATE</span>}
                                      </div>

                                      <div className="mt-1 grid grid-cols-2 gap-x-4 text-gray-600">
                                        <div>
                                          <span className="text-gray-400">Attendu : </span>
                                          <span className="font-medium">{formatTriggerValue(trigger.valuesLabels || trigger.values)}</span>
                                        </div>
                                        <div>
                                          <span className="text-gray-400">Réel : </span>
                                          <span className="font-medium">{formatTriggerValue(trigger.actualValueLabel ?? trigger.actualValue)}</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

const QuestionAnswerForm = ({ questionAnswers, onAdd, onRemove }) => {
  const [pending, setPending] = useState(null);
  const [pendingValue, setPendingValue] = useState("");
  const [lookupKey, setLookupKey] = useState(0);

  const onQuestionSelect = (question) => {
    if (!question) return;
    if (questionAnswers.some((qa) => qa.questionId === question._id)) {
      toast.error("Cette question est déjà ajoutée");
      setLookupKey((k) => k + 1);
      return;
    }
    setPending(question);
    setPendingValue("");
    setLookupKey((k) => k + 1);
  };

  const commitPending = (answer, optionValue = answer) => {
    if (!pending) return;
    if (!answer && pending.answerType !== "DOCUMENT") return;
    onAdd(pending, answer, optionValue);
    setPending(null);
    setPendingValue("");
  };

  const cancelPending = () => {
    setPending(null);
    setPendingValue("");
  };

  const isPendingMultipleChoice = pending?.answerType === "MULTIPLE_CHOICE" && pending?.options?.length > 0;
  const isPendingDocument = pending?.answerType === "DOCUMENT";

  return (
    <div className="space-y-2">
      {questionAnswers.map((qa) => (
        <div key={qa.questionId} className="flex items-start gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5">
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium text-gray-700">{qa.questionCode || qa.question}</p>
            <p className="truncate text-xs text-primary-600">{qa.optionValue}</p>
          </div>
          <button type="button" onClick={() => onRemove(qa.questionId)} className="shrink-0 text-gray-400 transition-colors hover:text-red-500" aria-label="Supprimer">
            <HiOutlineX className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}

      {pending && (
        <div className="space-y-2 rounded-md border border-primary-300 bg-primary-50/40 p-2">
          <div className="flex items-start gap-1.5">
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-gray-800">{pending.questionCode || pending.question}</p>
              {pending.questionCode && <p className="truncate text-[11px] text-gray-500">{pending.question}</p>}
            </div>
            <button type="button" onClick={cancelPending} className="shrink-0 text-gray-400 transition-colors hover:text-red-500" aria-label="Annuler">
              <HiOutlineX className="h-3.5 w-3.5" />
            </button>
          </div>

          {isPendingMultipleChoice ? (
            <div className="flex flex-wrap gap-1">
              {pending.options.map((option) => (
                <button
                  key={option._id}
                  type="button"
                  onClick={() => commitPending(option._id, option.value)}
                  className="rounded-md border border-primary-200 bg-white px-2 py-1 text-xs font-medium text-primary-700 transition-colors hover:border-primary-500 hover:bg-primary-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {option.value}
                </button>
              ))}
            </div>
          ) : isPendingDocument ? (
            <div className="flex flex-wrap gap-1">
              <button
                type="button"
                onClick={() => commitPending("PRESENT", "Document fourni")}
                className="rounded-md border border-primary-200 bg-white px-2 py-1 text-xs font-medium text-primary-700 transition-colors hover:border-primary-500 hover:bg-primary-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Document fourni
              </button>
              <button
                type="button"
                onClick={() => commitPending("", "Document non fourni")}
                className="rounded-md border border-primary-200 bg-white px-2 py-1 text-xs font-medium text-primary-700 transition-colors hover:border-primary-500 hover:bg-primary-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Document non fourni
              </button>
            </div>
          ) : pending.answerType === "DATE" ? (
            <input
              type="date"
              autoFocus
              value={pendingValue}
              onChange={(e) => {
                setPendingValue(e.target.value);
                if (e.target.value) commitPending(e.target.value);
              }}
              className="w-full rounded-md border border-gray-300 p-1.5 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
            />
          ) : pending.answerType === "HOUR" ? (
            <input
              type="time"
              autoFocus
              value={pendingValue}
              onChange={(e) => {
                setPendingValue(e.target.value);
                if (e.target.value) commitPending(e.target.value);
              }}
              className="w-full rounded-md border border-gray-300 p-1.5 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
            />
          ) : (
            <input
              type="text"
              autoFocus
              value={pendingValue}
              onChange={(e) => setPendingValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && pendingValue) {
                  e.preventDefault();
                  commitPending(pendingValue);
                }
              }}
              onBlur={() => {
                if (pendingValue) commitPending(pendingValue);
              }}
              placeholder="Saisir la réponse et Entrée"
              className="w-full rounded-md border border-gray-300 p-1.5 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
            />
          )}
        </div>
      )}

      <QuestionLookup key={lookupKey} onChange={onQuestionSelect} placeholder="Ajouter une question..." />
    </div>
  );
};

export default Simulator;
